import { useQuery } from "@apollo/client";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { StatCard, StatCardRowSkeleton } from "components/StatCard";
import { FacilityAccumulationSummaryQuery } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import pluralize from "pluralize";
import { Link as RouterLink } from "react-router-dom";

import { ComplianceFindingBadges } from "../../Compliance/ComplianceFindingBadge";
import {
  FACILITY_CAP_KINDS,
  useEntityComplianceFindings,
} from "../../Compliance/useEntityComplianceFindings";
import { StatCardGrid } from "../../components/StatCardGrid";
import { StatSubstats } from "../../components/StatSubstats";
import { FACILITY_ACCUMULATION_SUMMARY_QUERY } from "../../Containers/api";
import { formatContainerDate } from "../../Containers/containerDisplay";
import { isEventWindowLapsed } from "../../EpisodicEvents/episodicDisplay";
import { deriveOnSiteCapDisplay, formatKg } from "./onSiteCapDisplay";
import { useWasteFacilityState } from "./WasteFacilityContext";

type AccumulationSummary = NonNullable<
  NonNullable<FacilityAccumulationSummaryQuery["facility"]>["accumulationSummary"]
>;

/**
 * Container tracking summary for the waste facility dashboard: stat cards for
 * the active container count (with its deadline breakdown) and the on-site
 * hazardous total vs the generator-status cap, plus a link to the
 * facility-scoped container list.
 *
 * Half-width so it sits beside `WasteAccumulationAreasTile`, except when the
 * episodic-events tile is also on the dashboard: then Container Tracking
 * takes the full row on its own line, with Accumulation Areas and Episodic
 * events sharing the row below — see `ContainerAccumulationAndEpisodicTiles`
 * in `WasteFacility`. Area-level figures belong to that tile, not this one.
 */
type ActiveEpisodicEvent = NonNullable<
  NonNullable<FacilityAccumulationSummaryQuery["facility"]>["episodicEvents"]
>["items"][number];

/** The facility's current active episodic event, if any, for the banner. */
function selectActiveEpisodicEvent(
  data: FacilityAccumulationSummaryQuery | undefined
): ActiveEpisodicEvent | undefined {
  return data?.facility?.episodicEvents?.items[0];
}

/**
 * Banner copy for the active-episodic-event callout. The exemption the
 * summary resolver grants is revoked once the event's accumulation window
 * has lapsed but the event hasn't been swept/finalized yet
 * (`isEventWindowLapsed`), so the banner must stop asserting an exemption
 * that no longer applies and instead say the window ended. `now` is
 * injectable for tests.
 */
export function activeEventBannerMessage(
  event: Pick<ActiveEpisodicEvent, "reason" | "accumulationDeadline">,
  now: DateTime = DateTime.now()
): string {
  const deadline = formatContainerDate(event.accumulationDeadline);
  if (isEventWindowLapsed(event.accumulationDeadline, now)) {
    return `Episodic event active: ${event.reason} — its accumulation window ended ${deadline} and its waste no longer counts as exempt from the on-site cap.`;
  }
  return `Episodic event active: ${event.reason} — remove its waste by ${deadline}. Waste tied to the event doesn't count toward the on-site cap.`;
}

export const WasteContainersTile = ({
  fullWidth = false,
}: {
  /** Takes the full row width when the episodic-events tile is also present
   * (see `ContainerAccumulationAndEpisodicTiles` in `WasteFacility`). */
  fullWidth?: boolean;
}) => {
  const { tenantId } = useTenant();
  const { effectiveWasteFacilityId } = useWasteFacilityState();

  const { data, loading } = useQuery(FACILITY_ACCUMULATION_SUMMARY_QUERY, {
    variables: { facilityId: effectiveWasteFacilityId },
    skip: !effectiveWasteFacilityId,
    // Revalidate on mount so the on-site total reflects containers added from
    // other routes (create-container's refetch only hits this query while this
    // tile is mounted); cache-first alone leaves a stale total until hard refresh.
    fetchPolicy: "cache-and-network",
  });
  const summary = data?.facility?.accumulationSummary;
  const activeEvent = selectActiveEpisodicEvent(data);
  // The tile's mount is already ContainerTracking-gated in WasteFacility.
  const { forFacility } = useEntityComplianceFindings({
    facilityId: effectiveWasteFacilityId,
    kinds: FACILITY_CAP_KINDS,
  });

  return (
    <DashboardTile size={{ xs: 12, md: fullWidth ? 12 : 6 }}>
      <DashboardTile.Header title="Container Tracking" />
      <DashboardTile.ContentArea>
        <Stack spacing={2} alignItems="stretch" sx={{ width: "100%" }}>
          {loading && summary == null ? (
            <StatCardRowSkeleton count={3} />
          ) : (
            <AccumulationStatRow summary={summary} />
          )}
          <ComplianceFindingBadges findings={forFacility()} />
          {summary && (
            <SummaryCallouts
              summary={summary}
              activeEvent={activeEvent}
              tenantId={tenantId}
              facilityId={effectiveWasteFacilityId}
            />
          )}
          <Button
            component={RouterLink}
            to={`/o/${tenantId}/waste/containers?facilityId=${effectiveWasteFacilityId}`}
            variant="text"
            size="small"
            endIcon={<ArrowForward />}
            sx={{ alignSelf: "flex-start" }}
          >
            View containers
          </Button>
        </Stack>
      </DashboardTile.ContentArea>
    </DashboardTile>
  );
};

/**
 * The kg-on-site stat: the big number is the hazardous mass currently on
 * site; the bottom caption names the cap it's judged against — SQG/VSQG get
 * their cap, LQGs have none (time-limited instead), and an unresolvable
 * generator status shows no cap (the callout below prompts for setup). Once
 * an active episodic event exempts some of that mass, the caption switches
 * to the judged math and a sub-caption states the exempt amount.
 */
const OnSiteTotalStatCard = ({ summary }: { summary: AccumulationSummary | undefined }) => {
  const totalOnSiteKg = (summary?.nonAcuteOnSiteKg ?? 0) + (summary?.acuteOnSiteKg ?? 0);
  const display = deriveOnSiteCapDisplay(summary);

  return (
    <StatCard
      captionPlacement="top"
      value={formatKg(totalOnSiteKg)}
      caption={display.caption}
      // The acute line keeps its decimals: it's judged against a 1 kg cap,
      // where rounding 0.5 kg up to "1 kg" would misread as at-cap.
      subCaption={display.acuteCaption}
      extraCaption={display.exemptCaption}
      valueColor={summary?.overCap ? "error.main" : "text.primary"}
      subCaptionColor={display.acuteOver ? "error.main" : "text.secondary"}
    />
  );
};

const SummaryCallouts = ({
  summary,
  activeEvent,
  tenantId,
  facilityId,
}: {
  summary: AccumulationSummary;
  activeEvent: ActiveEpisodicEvent | undefined;
  tenantId: string | undefined;
  facilityId: string;
}) => (
  <>
    {activeEvent && (
      <Alert
        severity="info"
        action={
          <Button
            color="inherit"
            size="small"
            component={RouterLink}
            to={`/o/${tenantId}/waste/episodic-events?facilityId=${facilityId}`}
          >
            View event
          </Button>
        }
      >
        {activeEventBannerMessage(activeEvent)}
      </Alert>
    )}
    {summary.generatorStatus == null && (
      <Alert severity="info">Set your generator status to see the facility's on-site cap.</Alert>
    )}
    {summary.overCap && <Alert severity="error">Over the on-site accumulation cap</Alert>}
    {summary.assumedDensityContainerCount > 0 && (
      <Typography variant="caption" color="warning.main">
        {summary.assumedDensityContainerCount}{" "}
        {pluralize("container", summary.assumedDensityContainerCount)} converted using the density
        of water — totals may be inaccurate until{" "}
        {summary.assumedDensityContainerCount === 1 ? "its" : "their"} waste{" "}
        {pluralize("stream", summary.assumedDensityContainerCount)}{" "}
        {summary.assumedDensityContainerCount === 1 ? "has" : "have"} density information
      </Typography>
    )}
  </>
);

/** A count that reads as 0 rather than blank when the summary has not loaded. */
const count = (value: number | undefined) => value ?? 0;

/**
 * Non-zero counts are the ones worth looking at, so they take a semantic color
 * and everything else stays neutral. A helper rather than a ternary per card:
 * the rule is the same on every one of them.
 */
const emphasizeIfAny = (value: number | undefined, color: string) =>
  count(value) > 0 ? color : "text.primary";

/**
 * The active-container card, with the two deadline counts folded in as
 * substats.
 *
 * They used to be cards of their own beside this one, which read as three
 * independent figures when both are in fact subsets of the active count — an
 * overdue container is still an active container. Nesting them under the
 * headline number states the containment, and drops two cards from a tile
 * that is now half as wide.
 *
 * The headline itself stays neutral: "how many containers are open" is not a
 * problem, so the color lives on the substats that are.
 */
const ActiveContainersStatCard = ({ summary }: { summary: AccumulationSummary | undefined }) => {
  const active = count(summary?.activeContainerCount);

  return (
    <StatCard
      captionPlacement="top"
      caption={`Active ${pluralize("container", active)}`}
      value={String(active)}
      footer={
        // With nothing on site there are no deadlines to be clear of, so the
        // all-clear line would be claiming more than it knows.
        active > 0 ? (
          <StatSubstats
            allClearLabel="All within deadline"
            substats={[
              {
                count: count(summary?.approachingDeadlineContainerCount),
                label: "approaching deadline",
                color: "warning.main",
              },
              {
                count: count(summary?.overdueContainerCount),
                label: "overdue",
                color: "error.main",
              },
            ]}
          />
        ) : undefined
      }
    />
  );
};

/**
 * The facility's on-site numbers at a glance. Its own component because each
 * card carries a "highlight when non-zero" rule, and inlining those made the
 * tile itself hard to read.
 *
 * Satellite areas over their limit are deliberately absent: that count is the
 * Accumulation Areas tile's headline, and repeating it here made the same
 * problem look like two.
 */
const AccumulationStatRow = ({ summary }: { summary: AccumulationSummary | undefined }) => (
  <StatCardGrid>
    <ActiveContainersStatCard summary={summary} />
    <OnSiteTotalStatCard summary={summary} />
    {/* Warning, not error: the vendor re-weighing or re-classifying at
            the TSDF is normal and the attached document is the record of what
            actually left. This is something to review, not to fix. Document-
            agnostic caption: the count behind it includes both a manifest and
            a bill of lading. */}
    <StatCard
      captionPlacement="top"
      caption={`${pluralize("Shipment", count(summary?.discrepantShipmentCount))} differing from their paperwork`}
      value={String(count(summary?.discrepantShipmentCount))}
      valueColor={emphasizeIfAny(summary?.discrepantShipmentCount, "warning.main")}
    />
  </StatCardGrid>
);
